import { pathOr, path, clone } from 'ramda';

import { Ingredient } from '@/models/core/Ingredient';
import { PackItem } from '@/models/core/PackItem';
import {
  ProductPack,
  getProductPackReference,
  getProductPackSize,
  getProductPackName,
  getProductPackPrice,
  getProductPackUsage,
  isProductPackGenericEyeSerum,
  getProductPackGenericEyeSerumFormulaId,
  isProductPackSerum
} from '@/models/core/ProductPack';
import { getSizeValue, Price } from '@/models/core/values';
import {
  CartItem,
  getCartItemReference,
  getCartItemProductPack,
  getCartItemQuantity,
  getCartItemName
} from '@/models/core/CartItem';
import { getFormulaDataById } from '@/models/back-end/EyeSerumGenericFormulas';

import {
  DEFAULT_TREAT_SECTION_TREATMENT,
  PRODUCT_CATEGORY,
  PRODUCT_NAMES
} from '@/constants/treatment';

const getFormulaData = formulasOptions => {
  const oneFormula = pathOr({}, ['oneFormula'], formulasOptions);
  const twoFormulas = pathOr({}, ['twoFormulas'], formulasOptions);

  return [oneFormula, twoFormulas].find(({ isFound }) => isFound);
};

const getSerumBottleCompositions = formulasOptions => {
  const formulaData = getFormulaData(formulasOptions);

  const firstBottle = pathOr(null, ['compositionBottle1'], formulaData);
  const secondBottle = pathOr(null, ['compositionBottle2'], formulaData);

  return [firstBottle, secondBottle].filter(bottle => bottle !== null);
};

const getSerumUsageFromFormulasOptions = formulasOptions => {
  const formulaData = getFormulaData(formulasOptions);

  return pathOr(null, ['usage'], formulaData);
};

const getIngredients = (bottleComposition, countryCode) => {
  const first = pathOr(null, ['actif1'], bottleComposition);
  const second = pathOr(null, ['actif2'], bottleComposition);
  const third = pathOr(null, ['actif3'], bottleComposition);

  const actives = [first, second, third].filter(active => active !== null);

  return actives
    .filter(({ name }) => name !== '')
    .map(({ name, nbOfCapsules }) => Ingredient(name, nbOfCapsules, countryCode));
};

const SECTION_NAME_IN_TREATMENT_DTO = {
  faceSerum: 'treatFace',
  eyeContourSerum: 'treatEyes'
};

const getSerumProductPack = (
  productReference,
  treatmentDto,
  usePriceFromTreatmentDto = false,
  currency = ''
) => {
  if (usePriceFromTreatmentDto && !currency) {
    throw Error('Invalid function usage. Missing argument "currency"');
  }

  const formulasOptions = pathOr(
    null,
    ['treatmentDetails', SECTION_NAME_IN_TREATMENT_DTO[productReference], 'formulasOptions'],
    treatmentDto
  );
  const compositions = getSerumBottleCompositions(formulasOptions);

  if (compositions.length === 0) {
    return null;
  }

  const countryCode = pathOr(undefined, ['customerCountryCode'], treatmentDto);
  const [firstBottleIngredients, secondBottleIngredients] = compositions.map(composition =>
    getIngredients(composition, countryCode)
  );

  const packUsage = getSerumUsageFromFormulasOptions(formulasOptions);

  const reference = productReference;
  const categories = ['treat'];

  const firstBottleUsage = packUsage === 'oneMorningOneEvening' ? 'morningOnly' : packUsage;
  const secondBottleUsage = packUsage === 'oneMorningOneEvening' ? 'eveningOnly' : packUsage;
  const totalPackCost = pathOr(
    null,
    ['treatmentDetails', SECTION_NAME_IN_TREATMENT_DTO[productReference], 'cost'],
    treatmentDto
  );

  const price = usePriceFromTreatmentDto ? Price(Number(totalPackCost), currency) : undefined;

  const packItems = [
    PackItem({
      reference,
      usage: firstBottleUsage,
      categories,
      ingredients: firstBottleIngredients,
      price
    })
  ];

  if (secondBottleIngredients) {
    packItems.push(
      PackItem({
        reference,
        usage: secondBottleUsage,
        categories,
        ingredients: secondBottleIngredients,
        price
      })
    );
  }

  return ProductPack(packItems);
};

const PRODUCT_CATEGORY_IN_TREATMENT_DTO = {
  [PRODUCT_NAMES.FACE_SERUM]: PRODUCT_CATEGORY.TREAT,
  [PRODUCT_NAMES.EYE_CONTOUR_SERUM]: PRODUCT_CATEGORY.TREAT,

  [PRODUCT_NAMES.NEXULTRA_O]: PRODUCT_CATEGORY.CLEANSE,
  [PRODUCT_NAMES.GEL]: PRODUCT_CATEGORY.CLEANSE,

  [PRODUCT_NAMES.NEXULTRA_P_LIGHT]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_P_RICH]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_1]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_2]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_3]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_B]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.NEXULTRA_L]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.UNIVERSKIN_H]: PRODUCT_CATEGORY.MOISTURIZE,
  [PRODUCT_NAMES.SUNSCREEN_SPF]: PRODUCT_CATEGORY.PROTECT,
  [PRODUCT_NAMES.SUNSCREEN_CRM]: PRODUCT_CATEGORY.PROTECT,
  [PRODUCT_NAMES.SUNSCREEN_FLD]: PRODUCT_CATEGORY.PROTECT,
  [PRODUCT_NAMES.NEXULTRA_UV]: PRODUCT_CATEGORY.PROTECT,
  [PRODUCT_NAMES.UNIVERSKIN_M]: PRODUCT_CATEGORY.PROTECT
};

const setInPlaceSerumCartItemToTreatment = (cartItem, treatmentDto, eyeSerumGenericFormulasDto) => {
  const pack = getCartItemProductPack(cartItem);

  if (isProductPackGenericEyeSerum(pack)) {
    // generic formulas adding should be handled differently
    // from normal serum adding
    const formulaId = getProductPackGenericEyeSerumFormulaId(pack);
    const formulaData = getFormulaDataById(formulaId, eyeSerumGenericFormulasDto);

    if (!formulaData) {
      throw Error('Generic formula data is not found');
    }

    const treatmentDetails = path(['treatmentDetails'], treatmentDto);

    treatmentDetails.treatEyes = formulaData.formula;

    return;
  }

  // dont change quantity as it meant 1 always
  const section = SECTION_NAME_IN_TREATMENT_DTO[getProductPackName(pack)];

  const sectionData = path(['treatmentDetails', section], treatmentDto);
  sectionData.isRecommended = true;
};

const setInPlaceComplementaryCartItemToTreatment = (cartItem, treatmentDto) => {
  const pack = getCartItemProductPack(cartItem);
  const category = PRODUCT_CATEGORY_IN_TREATMENT_DTO[getProductPackName(pack)];
  const reference = getProductPackReference(pack);
  const productName = getProductPackName(pack);
  const products = path(['treatmentDetails', category, 'products'], treatmentDto);

  // \d* rather than \d?: a size suffix is not always a single digit. The gel is sold as gel30
  // and gel100, so \d? matched neither, the existing entry was not found, and a duplicate was
  // appended while the original kept orderMultiplier 0 — which the back end rejects with a 500.
  const referenceRegex = new RegExp(`^${productName}\\d*$`);

  const product = products.find(p => p.name.match(referenceRegex));

  if (product) {
    product.name = reference; // in case size was changed hence the reference
    product.size = getSizeValue(getProductPackSize(pack)).toString();
    product.orderMultiplier = getCartItemQuantity(cartItem);
  } else {
    // The patient added a product the doctor did not propose, so the section holds no entry
    // for it. Without this branch the cart item is dropped from the payload: the patient is
    // charged for it on screen, but it never reaches the treatment, the order or Odoo.
    // The back end assigns the ADDED_BY_PATIENT status itself.
    //
    // isRecommended must be true. On a product it means "part of the offer", not "the doctor
    // recommended it" — that distinction is carried by the status. The back end prices a
    // section by summing only its products with isRecommended true
    // (PriceCalculatorClientUtil.getPrice), while the Odoo transfer sends every product that
    // is not REMOVED_BY_PATIENT. Setting it false therefore ships the product without
    // charging for it.
    products.push({
      name: reference,
      size: getSizeValue(getProductPackSize(pack)).toString(),
      price: getProductPackPrice(pack).value,
      usage: getProductPackUsage(pack),
      multiplier: 1,
      isRecommended: true,
      orderMultiplier: getCartItemQuantity(cartItem)
    });
  }
};

const filterOutInPlaceComplementaryProductsFromTreatmentDto = (
  whiteListComplementaryReferences,
  treatmentDto
) => {
  const filterProducts = products =>
    products.filter(({ name }) => whiteListComplementaryReferences.includes(name));

  const sectionProducts = {
    cleanse: filterProducts(path(['treatmentDetails', 'cleanse', 'products'], treatmentDto)),
    moisturize: filterProducts(path(['treatmentDetails', 'moisturize', 'products'], treatmentDto)),
    protect: filterProducts(path(['treatmentDetails', 'protect', 'products'], treatmentDto))
  };

  ['cleanse', 'moisturize', 'protect'].forEach(section => {
    // eslint-disable-next-line no-param-reassign
    treatmentDto.treatmentDetails[section].products = sectionProducts[section];
    // eslint-disable-next-line no-param-reassign
    treatmentDto.treatmentDetails[section].isRecommended = sectionProducts[section].length > 0;
  });
};

/**
 * Get updated treatmentDto with cart items added to it.
 *
 * It does not mutate existing treatmentDto, but creates a new copy of it.
 *
 * @param {CartItem[]} cartItems
 * @param {Object} treatmentDto
 * @param {Object} eyeSerumGenericFormulasDto
 */
export const getTreatmentDtoForSaveCartRequest = (
  cartItems,
  treatmentDto,
  eyeSerumGenericFormulasDto
) => {
  const updatedTreatment = clone(treatmentDto);
  let doesCartContainFaceSerum = false;
  let doesCartContainEyeContourSerum = false;

  cartItems.forEach(item => {
    const pack = getCartItemProductPack(item);
    const name = getProductPackName(pack);

    if (['faceSerum', 'eyeContourSerum'].includes(name)) {
      setInPlaceSerumCartItemToTreatment(item, updatedTreatment, eyeSerumGenericFormulasDto);

      if (name === 'faceSerum') {
        doesCartContainFaceSerum = true;
      } else {
        doesCartContainEyeContourSerum = true;
      }
    } else {
      setInPlaceComplementaryCartItemToTreatment(item, updatedTreatment);
    }
  });

  const whiteListComplementaryReferences = cartItems.map(getCartItemReference);
  filterOutInPlaceComplementaryProductsFromTreatmentDto(
    whiteListComplementaryReferences,
    updatedTreatment
  );

  // back-end interpret face serum differently from complementary products;
  // we need to reset the serum section if 0 serum items in cart
  if (!doesCartContainFaceSerum) {
    updatedTreatment.treatmentDetails.treatFace = DEFAULT_TREAT_SECTION_TREATMENT;
  }

  if (!doesCartContainEyeContourSerum) {
    updatedTreatment.treatmentDetails.treatEyes = DEFAULT_TREAT_SECTION_TREATMENT;
  }

  return updatedTreatment;
};

/**
 * Get serum product packs from treatmentDto.
 *
 * @param {Object} treatmentDto
 * @param {boolean} [usePriceFromTreatmentDto] - should we trust price data in treatmentDto
 * @param {string} [currency]
 * @return {ProductPack[]}
 */
export const getSerumProductPacks = (
  treatmentDto,
  usePriceFromTreatmentDto = false,
  currency = ''
) => {
  return [
    getSerumProductPack('faceSerum', treatmentDto, usePriceFromTreatmentDto, currency),
    getSerumProductPack('eyeContourSerum', treatmentDto, usePriceFromTreatmentDto, currency)
  ].filter(productPack => productPack !== null);
};

export const getComplementaryProductDataFromTreatmentDto = (productPack, treatmentDto) => {
  const category = PRODUCT_CATEGORY_IN_TREATMENT_DTO[getProductPackName(productPack)];
  const reference = getProductPackReference(productPack);

  const productData = pathOr([], ['treatmentDetails', category, 'products'], treatmentDto).find(
    product => product.name === reference
  );

  return productData || null;
};

export const getPrescribedComplementaryProductUsage = (productPack, treatmentDto) => {
  const data = getComplementaryProductDataFromTreatmentDto(productPack, treatmentDto);

  return data ? data.usage : null;
};

export const getPrescribedProductQuantityFromRenewTreatmentDto = (
  productPack,
  renewTreatmentDto
) => {
  if (isProductPackSerum(productPack)) {
    const section = SECTION_NAME_IN_TREATMENT_DTO[getProductPackName(productPack)];
    const oneFormula = pathOr(
      false,
      ['treatmentDetails', section, 'formulasOptions', 'oneFormula', 'isFound'],
      renewTreatmentDto
    );
    const twoFormulas = pathOr(
      false,
      ['treatmentDetails', section, 'formulasOptions', 'twoFormulas', 'isFound'],
      renewTreatmentDto
    );

    return oneFormula || twoFormulas ? 1 : 0;
  }

  const data = getComplementaryProductDataFromTreatmentDto(productPack, renewTreatmentDto);

  return data ? data.orderMultiplier : 0;
};

export const isRenewedOnlineTreatment = treatmentDto =>
  treatmentDto.isRenewedTreatment && treatmentDto.basedOnOnlinePrototypeOffer;

export const getTreatmentId = treatmentDto => treatmentDto.id;

/**
 * Get previously bought cart items.
 *
 * @param {ProductPack[]} productPacks - all product packs
 * @param {Object} prototypeTreatmentDto
 * @return {CartItem[]}
 */
export const getPreviouslyBoughtCartItemsFromRenewedTreatment = (
  productPacks,
  prototypeTreatmentDto
) => {
  return productPacks.reduce((list, pack) => {
    const quantity = getPrescribedProductQuantityFromRenewTreatmentDto(pack, prototypeTreatmentDto);

    if (quantity > 0) {
      list.push(CartItem(pack, quantity));
    }

    return list;
  }, []);
};

/**
 * Get previously bought product names.
 *
 * @param {ProductPack[]} productPacks - all product packs
 * @param {Object} prototypeTreatmentDto
 * @return {ProductPack[]}
 */
export const getPreviouslyBoughtProductNamesFromRenewedTreatment = (
  productPacks,
  prototypeTreatmentDto
) =>
  getPreviouslyBoughtCartItemsFromRenewedTreatment(productPacks, prototypeTreatmentDto).map(
    getCartItemName
  );
